import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ChatBubble, ChatBubbleAvatar, ChatBubbleMessage } from '../../components/assistant/chat/chat-bubble';
import { ChatMessageList } from '../../components/assistant/chat/chat-message-list';
import { Loadable } from '../../components/loaders';
import { useAbortSignal } from '../../hooks/useAbortSignal';
import { RootState } from '../../reducers';
import { useLazyGetThreadQuery, useSendMessageMutation } from '../../services/assistant';
import { AssistantMessage, AssistantMessageType } from '../../services/assistant/types';
import { useWorkspace } from '../workspaces/hooks';
import { AssistantInput } from './AssistantInput';
import { AssistantMenu } from './AssistantMenu';
import { QuestionCard } from './QuestionCard';
import { setThreadId } from './assistantSlice';
import { SEND_MESSAGE_RTK_KEY } from './constants';

type AssistantChatProps = {
  handleClose: VoidFunction;
};

export const AssistantChat: FC<AssistantChatProps> = () => {
  const { signal } = useAbortSignal();

  const { t } = useTranslation('assistant');
  const { id: workspaceId } = useWorkspace();
  const dispatch = useDispatch();

  const [thread, setThread] = useState<AssistantMessage[]>([]);

  const currentThreadId = useSelector((state: RootState) => state.assistant.threadId);
  const [initialThreadId, setInitialThreadId] = useState<string | undefined>(currentThreadId);

  const [sendMessage, { isLoading: isMessageSending }] = useSendMessageMutation({ fixedCacheKey: SEND_MESSAGE_RTK_KEY });

  const [getThread, { isFetching: isThreadFetching }] = useLazyGetThreadQuery();
  useEffect(() => {
    const fetchThread = async () => {
      if (initialThreadId) {
        const response = await getThread({
          workspaceId,
          thread_id: initialThreadId,
          signal,
        });

        if ('data' in response) {
          setThread(response.data.messages);
        }
      }
    };

    fetchThread();
  }, [initialThreadId]);

  const isGenerating = isMessageSending;

  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messagesRef.current]);

  const handleSendMessage = async (question: string) => {
    setThread((prev) => [...prev, { content: question, type: AssistantMessageType.Human }]);
    const response = await sendMessage({
      question,
      workspaceId,
      thread_id: currentThreadId,
      signal,
    });

    if ('data' in response) {
      setThread(response.data.messages);
    }
  };

  const handleDeleteThread = () => {
    dispatch(setThreadId(undefined));
    setThread([]);
    setInitialThreadId(undefined);
  };

  return (
    <div className='relative flex w-[450px] flex-col gap-y-3 rounded-lg border bg-white-100 p-4 shadow-md'>
      <Loadable isLoading={isThreadFetching} className='min-h-[50dvh]'>
        {thread.length > 0 ? <AssistantMenu handleDeleteThread={handleDeleteThread} /> : null}
        <ChatMessageList>
          {!thread && !isGenerating ? <div className='place-self-center text-lg font-medium'>{t('howCanIHelpYouToday')}</div> : null}
          {thread?.map((msg) => (
            <ChatBubble key={msg.content} variant={msg.type === 'human' ? 'sent' : 'received'}>
              {msg.type === 'ai' && <ChatBubbleAvatar fallback={'🤖'} />}
              <ChatBubbleMessage variant={msg.type === 'human' ? 'sent' : 'received'}>{msg.content}</ChatBubbleMessage>
            </ChatBubble>
          ))}
          {isGenerating && (
            <ChatBubble variant='received'>
              <ChatBubbleAvatar fallback='🤖' />
              <ChatBubbleMessage isLoading />
            </ChatBubble>
          )}
          {/* <ChatBubble key={'123'} variant={'received'}>
            {<ChatBubbleAvatar fallback={'🤖'} />}
            <ChatBubbleMessage variant={'received'}>
              {
                "You have the following data sources:<br/>1. [orders](http://localhost:3000//data-sources/s-RKsEoyJ9yheB5ynn)<br/>2. [order4](http://localhost:3000//data-sources/s-hHHAy4zS27DIiEqH)<br/>3. [Custom Orders 4](http://localhost:3000//data-sources/s-LDKnEfaUcL5Ombsl)<br/>4. [Custom Orders](http://localhost:3000//data-sources/s-9DDY07zdTH1DWnju)<br/>5. [Custom Orders 2](http://localhost:3000//data-sources/s-X6zfCYV7vbqkk9X7)<br/>6. [Orders New](http://localhost:3000//data-sources/s-fXjxNBEkGvcPD8eS)<br/>7. [Order from July '23 - July '24](http://localhost:3000//data-sources/s-CvnE4vDBdxhsWWVP)<br/>8. [Octavia Rose](http://localhost:3000//data-sources/s-GrNc88K0FecaQX05)"
              }
            </ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={1} variant={'sent'}>
            <ChatBubbleMessage variant={'sent'}>{'123123 123123 123123 123123123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={2} variant={'received'}>
            <ChatBubbleAvatar fallback='🤖' />
            <ChatBubbleMessage variant={'received'}>{'123123 123123 123123 123123 123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={3} variant={'sent'}>
            <ChatBubbleMessage variant={'sent'}>{'123123 123123 123123 123123123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={4} variant={'received'}>
            <ChatBubbleAvatar fallback='🤖' />
            <ChatBubbleMessage variant={'received'}>{'123123 123123 123123 123123 123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={5} variant={'sent'}>
            <ChatBubbleMessage variant={'sent'}>{'123123 123123 123123 123123123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={6} variant={'received'}>
            <ChatBubbleAvatar fallback='🤖' />
            <ChatBubbleMessage variant={'received'}>{'123123 123123 123123 123123 123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={7} variant={'sent'}>
            <ChatBubbleMessage variant={'sent'}>{'123123 123123 123123 123123123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble>
          <ChatBubble key={8} variant={'received'}>
            <ChatBubbleAvatar fallback='🤖' />
            <ChatBubbleMessage variant={'received'}>{'123123 123123 123123 123123 123123 123123 123123 123123 123123 123123'}</ChatBubbleMessage>
          </ChatBubble> */}
        </ChatMessageList>
        {thread && thread.length > 0 ? null : (
          <div className='grid grid-cols-2 gap-x-4'>
            <QuestionCard onClick={handleSendMessage} disabled={isGenerating} question={t('whatCanIDoWithThePlatform')} />
            <QuestionCard onClick={handleSendMessage} disabled={isGenerating} question={t('HowDoIUploadData')} />
          </div>
        )}
      </Loadable>
      <AssistantInput isLoading={isThreadFetching} handleSendMessage={handleSendMessage} />
    </div>
  );
};
